import { useEffect, useRef, useState } from "react";
import { Download, Share2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";

const W = 1080, H = 1350, LETTERS = ["C", "A", "M", "R", "Y", "N"];

async function draw(cv, d, showWeight) {
  await Promise.all(["600 120px Fraunces", "italic 400 60px Fraunces", "600 32px Figtree", "400 30px Figtree"].map((f) => document.fonts.load(f).catch(() => null)));
  const c = cv.getContext("2d");
  const cur = d.phases[d.phases.length - 1] || { color: "#C86D51", name: "", letter: "C" };
  c.fillStyle = "#2D2926"; c.fillRect(0, 0, W, H);
  const g = c.createRadialGradient(W - 120, 160, 20, W - 120, 160, 700);
  g.addColorStop(0, cur.color + "88"); g.addColorStop(1, "#2D292600");
  c.fillStyle = g; c.fillRect(0, 0, W, H);
  c.textBaseline = "alphabetic";

  c.font = "600 30px Figtree"; c.fillStyle = "rgba(255,255,255,0.6)";
  c.fillText((d.final ? `YEAR ${d.year} · MY YEAR-END REVEAL` : `YEAR ${d.year} · WEEK ${d.week} OF ${d.total_weeks}`).split("").join(String.fromCharCode(8202)), 90, 140);

  const reached = d.phases.length;
  c.font = "600 150px Fraunces";
  LETTERS.forEach((l, i) => {
    c.fillStyle = i < reached ? (d.phases[i]?.color || "#fff") : "rgba(255,255,255,0.16)";
    c.fillText(l, 90 + i * 155, 330);
  });

  const first = (d.name || "").split(" ")[0];
  c.fillStyle = "#FBF9F5"; c.font = "italic 400 76px Fraunces";
  c.fillText(d.final ? `${first}'s year` : `${first}'s year so far`, 90, 480);
  c.font = "400 34px Figtree"; c.fillStyle = "rgba(255,255,255,0.75)";
  c.fillText(`Chapter ${reached}: ${cur.letter} · ${cur.name}`, 90, 540);

  const stats = [[d.days_showed_up, "days showed up"], [d.steps_done, "steps done"], [d.wins.length, "real wins"]];
  stats.forEach(([n, l], i) => {
    const x = 90 + i * 310;
    c.fillStyle = "#FBF9F5"; c.font = "600 120px Fraunces"; c.fillText(String(n), x, 760);
    c.fillStyle = "rgba(255,255,255,0.6)"; c.font = "400 30px Figtree"; c.fillText(l, x, 810);
  });

  c.fillStyle = "rgba(255,255,255,0.15)"; c.fillRect(90, 880, W - 180, 2);
  const best = d.pillars.filter((p) => p.then != null && p.now != null).sort((a, b) => (b.now - b.then) - (a.now - a.then))[0];
  let y = 975;
  c.font = "400 32px Figtree"; c.fillStyle = "rgba(255,255,255,0.6)";
  if (best && best.now > best.then) {
    c.fillText("Biggest change", 90, y);
    c.fillStyle = "#FBF9F5"; c.font = "600 56px Fraunces"; c.fillText(`${best.label}  ${best.then} → ${best.now}`, 90, y + 70); y += 170;
  }
  if (showWeight && d.weight && d.weight.lost > 0) {
    const lb = d.weight.unit === "lb";
    c.font = "400 32px Figtree"; c.fillStyle = "rgba(255,255,255,0.6)"; c.fillText("On my trend line", 90, y);
    c.fillStyle = "#F3C98B"; c.font = "600 56px Fraunces"; c.fillText(`${(d.weight.lost * (lb ? 2.20462 : 1)).toFixed(1)} ${d.weight.unit} lighter`, 90, y + 70);
  }
  c.fillStyle = "rgba(255,255,255,0.5)"; c.font = "600 28px Figtree";
  c.fillText("CAMRYN", 90, H - 90);
  c.font = "400 28px Figtree"; c.fillText("·  a year that's yours", 230, H - 90);
}

export function ShareCard({ d, open, onOpenChange }) {
  const ref = useRef(null);
  const [weight, setWeight] = useState(false);
  const [url, setUrl] = useState(null);
  const [canShare, setCanShare] = useState(false);

  useEffect(() => {
    if (!open) return;
    const cv = document.createElement("canvas"); cv.width = W; cv.height = H; ref.current = cv;
    draw(cv, d, weight).then(() => {
      setUrl(cv.toDataURL("image/png"));
      cv.toBlob((b) => {
        const f = new File([b], "my-camryn-year.png", { type: "image/png" });
        ref.current.file = f;
        setCanShare(!!navigator.canShare && navigator.canShare({ files: [f] }));
      });
    });
  }, [open, weight, d]);

  const share = async () => { try { await navigator.share({ files: [ref.current.file], title: "My CAMRYN year" }); } catch (e) { if (e?.name !== "AbortError") console.error("Share failed", e); } };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] overflow-y-auto" data-testid="share-card-dialog">
        <DialogHeader><DialogTitle className="font-display">Share your year</DialogTitle></DialogHeader>
        <div className="rounded-2xl overflow-hidden bg-[#2D2926] aspect-[4/5]">{url && <img src={url} alt="Your CAMRYN year card" className="w-full h-full object-cover" data-testid="share-card-preview" />}</div>
        {d.weight && d.weight.lost > 0 && (
          <label className="flex items-center justify-between text-sm"><span><span className="font-medium block">Show my weight change</span><span className="text-xs text-stone-600">Off by default. Only you decide.</span></span>
            <Switch checked={weight} onCheckedChange={setWeight} data-testid="share-weight-switch" /></label>
        )}
        <p className="text-xs text-stone-600">Your why, notes, memories and letters are never on the card.</p>
        <div className="flex gap-2">
          {canShare && <button onClick={share} data-testid="share-card-share-btn" className="btn-ink flex-1 h-11 rounded-full text-sm font-semibold inline-flex items-center justify-center gap-2"><Share2 className="h-4 w-4" />Share</button>}
          <a href={url || "#"} download="my-camryn-year.png" data-testid="share-card-download-btn" className={`${canShare ? "border border-stone-300" : "btn-ink"} flex-1 h-11 rounded-full text-sm font-semibold inline-flex items-center justify-center gap-2`}><Download className="h-4 w-4" />Save image</a>
        </div>
      </DialogContent>
    </Dialog>
  );
}
